package com.eladfinish.bible.contest.screens.randomVerse.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.tooling.preview.PreviewParameterProvider
import androidx.navigation.NavController
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.at_least_one_book
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.copied_to_clipboard
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.domain.catalog.BibleCatalog
import com.eladfinish.bible.contest.domain.catalog.BookData
import com.eladfinish.bible.contest.mikud
import com.eladfinish.bible.contest.navigation.RandomVerseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.screenKey
import com.eladfinish.bible.contest.pasuk_example
import com.eladfinish.bible.contest.platform.audio.SoundEffect
import com.eladfinish.bible.contest.platform.audio.SoundService
import com.eladfinish.bible.contest.platform.haptics.HapticService
import com.eladfinish.bible.contest.platform.shake.ShakeController
import com.eladfinish.bible.contest.screens.mikud.model.mikudItem
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseAction
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseEffect
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseState
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseState.BottomSheetState
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseStore
import com.eladfinish.bible.contest.screens.randomVerse.RandomVerseStore.Companion.RandomVerseStoreParams
import com.eladfinish.bible.contest.screens.randomVerse.model.RandomVersePart
import com.eladfinish.bible.contest.screens.randomVerse.model.RandomizedVerse
import com.eladfinish.bible.contest.screens.randomVerse.ui.dialogs.RandomVerseChapterPickerSheet
import com.eladfinish.bible.contest.screens.randomVerse.ui.dialogs.RandomVerseDialogs
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.settings.ui.screens.KEY_RANDOM_VERSE_REFRESH
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.ui.animations.rememberScaleAnimation
import com.eladfinish.bible.contest.ui.animations.rememberShakeAnimation
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.ParchmentView
import com.eladfinish.bible.contest.ui.components.ShakeBindings
import com.eladfinish.bible.contest.ui.menus.actions.RandomVerseToolbarAction
import com.eladfinish.bible.contest.ui.menus.viewModels.ToolbarActionBusViewModel
import com.eladfinish.bible.contest.ui.scaffold.ShowSnackbar
import com.eladfinish.bible.contest.utils.addGershaim
import com.eladfinish.bible.contest.utils.toOtiot
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.flow.Flow
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlinx.collections.immutable.persistentListOf as persistentListOfVerseUiModel

@Composable
fun RandomVerseScreen(
    randomVerseStore: RandomVerseStore = koinViewModel(
        parameters = {
            parametersOf(
                RandomVerseStoreParams(
                    enableHalfVerseDefault = BooleanConstants.ENABLE_HALF_VERSE_DEFAULT,
                    enableParashaIdentifyingTask = BooleanConstants.ENABLE_PARASHA_IDENTIFYING_TASK,
                )
            )
        }
    ),
    toolbarActionBus: ToolbarActionBusViewModel,
    navController: NavController,
    openMikud: () -> Unit,
    openBrowse: (bookIndex: Int, chapterIndex: Int) -> Unit,
    openSeveralRandomVerses: () -> Unit,
    openRandomVerseSettings: () -> Unit,
    openVerseWidget: () -> Unit,
    showToastStringResource: (StringResource) -> Unit,
    showSnackbar: ShowSnackbar,
    dismissSnackbar: () -> Boolean,
    animationState: AnimationState = AnimationState.OFF,
    hapticService: HapticService = getKoinInstance(),
    soundService: SoundService = getKoinInstance(),
    shakeController: ShakeController = getKoinInstance(),
) {
    val currentOpenMikud by rememberUpdatedState(openMikud)
    val currentOpenBrowse by rememberUpdatedState(openBrowse)
    val currentOpenSeveralRandomVerses by rememberUpdatedState(openSeveralRandomVerses)
    val currentOpenRandomVerseSettings by rememberUpdatedState(openRandomVerseSettings)
    val currentOpenVerseWidget by rememberUpdatedState(openVerseWidget)
    val currentShowToastStringResource by rememberUpdatedState(showToastStringResource)
    val currentDismissSnackbar by rememberUpdatedState(dismissSnackbar)

    ObserveRefreshAfterSettings(
        navController = navController,
        onRefresh = {
            randomVerseStore.dispatch(
                RandomVerseAction.RefreshAfterSettings,
            )
        },
    )

    RandomVerseExternalBindings(
        randomVerseStore = randomVerseStore,
        toolbarActionBus = toolbarActionBus,
        hapticService = hapticService,
        shakeController = shakeController,
    )

    var animateWrongAnswer by remember { mutableStateOf(false) }

    RandomVerseSideEffectsCollector(
        effects = randomVerseStore.observeSideEffect(),
        onCopyToClipboard = { currentShowToastStringResource(SharedRes.strings.copied_to_clipboard) },
        onCorrectAnswer = {
            hapticService.vibrateIfAllowed(milliseconds = 320)
            soundService.playIfAllowed(effect = SoundEffect.CORRECT)
        },
        onWrongAnswer = {
            hapticService.vibrateIfAllowed(milliseconds = 80)
            soundService.playIfAllowed(effect = SoundEffect.WRONG)
            animateWrongAnswer = true
        },
        onWrongAnswerOnChapter = {
            hapticService.vibrateIfAllowed(milliseconds = 60)
        },
        onCorrectAnswerOnChapter = {
            hapticService.vibrateIfAllowed(milliseconds = 240)
        },
        onOpenSeveralRandomVerses = {
            currentOpenSeveralRandomVerses()
        },
        onOpenRandomVerseSettings = {
            currentOpenRandomVerseSettings()
        },
        onOpenVerseWidget = {
            currentOpenVerseWidget()
        },
        onNotifyEmptyMikud = {
            showSnackbar(
                SharedRes.strings.at_least_one_book.desc(),
                SharedRes.strings.mikud.desc(),
                SnackbarDuration.Short,
            ) {
                currentOpenMikud()
            }
        },
        onDismissSnackbar = currentDismissSnackbar,
    )

    val state by randomVerseStore.observeState().collectAsState()

    RandomVerseScreenUi(
        state = state,
        animationState = animationState,
        animateWrongAnswer = animateWrongAnswer,
        resetWrongAnswerAnimation = { animateWrongAnswer = false },
        onVerseClick = { randomVerseStore.dispatch(RandomVerseAction.VerseClicked) },
        onVerseLongClick = { randomVerseStore.dispatch(RandomVerseAction.VerseLongClicked) },
        onMikudClick = {
            currentDismissSnackbar()
            currentOpenMikud() // TODO: dispatch action when you add it
        },
        onBookClick = { randomVerseStore.dispatch(RandomVerseAction.BookClicked(it)) },
        onWidgetPromptClick = { randomVerseStore.dispatch(RandomVerseAction.WidgetPromptClicked) },
        onWidgetPromptDismiss = { randomVerseStore.dispatch(RandomVerseAction.WidgetPromptDismissed) },
        isCorrect = { state.randomizedVerse.verseUiModel.source.substringAfter("(").substringBefore(" ") == it },
        dialogContent = {
            RandomVerseDialogs(
                dialogState = state.dialogState,
                onAction = randomVerseStore::dispatch,
                onChapterDialogTitleClick = { dialogItem -> // TODO: dispatch action
                    currentDismissSnackbar()
                    currentOpenBrowse(
                        dialogItem.bookIndex!!,
                        dialogItem.chapterIndex!!,
                    )
                },
            )
        },
        bottomSheetContent = {
            RandomVerseChapterPickerSheet(
                bottomSheetState = state.bottomSheetState,
                onAction = randomVerseStore::dispatch,
            )
        }
    )
}

// This listener observes a SavedStateHandle flag that is set when returning
// from the Settings screen. When the flag becomes true, it consumes the event
// (resets it to false) and triggers a refresh in this screen.
//
// If you need a reusable solution for other screens, you can generalize this
// into something like ObserveSavedStateFlag(key: String, onTriggered: () -> Unit).
// For this specific use case, ObserveRefreshAfterSettings keeps the intent clear.
@Composable
private fun ObserveRefreshAfterSettings(
    navController: NavController,
    onRefresh: () -> Unit,
) {
    val backStackEntry = navController.currentBackStackEntry

    val refreshFlow = remember(backStackEntry) {
        backStackEntry
            ?.savedStateHandle
            ?.getStateFlow(
                key = KEY_RANDOM_VERSE_REFRESH,
                initialValue = false,
            )
    }

    LaunchedEffect(refreshFlow) {
        refreshFlow?.collect { shouldRefresh ->
            if (!shouldRefresh) return@collect

            // Consume the event
            backStackEntry?.savedStateHandle?.set(
                key = KEY_RANDOM_VERSE_REFRESH,
                value = false,
            )

            onRefresh()
        }
    }
}

/**
 * Binds external event sources to the store:
 * - Toolbar bus
 * - Shake detector
 */
@Composable
private fun RandomVerseExternalBindings(
    randomVerseStore: RandomVerseStore,
    toolbarActionBus: ToolbarActionBusViewModel,
    hapticService: HapticService,
    shakeController: ShakeController,
) {
    RandomVerseToolbarBindings(
        onAction = randomVerseStore::dispatch,
        toolbarActionBus = toolbarActionBus,
    )

    val state by randomVerseStore.observeState().collectAsState()

    ShakeBindings(
        isShakeEnabled = state.isShakeEnabled,
        shakeController = shakeController,
        onShaken = {
            randomVerseStore.dispatch(RandomVerseAction.DeviceIsShaken)
            hapticService.vibrateIfAllowed(milliseconds = 20)
        },
    )
}

@Composable
private fun RandomVerseToolbarBindings(
    onAction: (RandomVerseAction) -> Unit,
    toolbarActionBus: ToolbarActionBusViewModel,
) {
    val screenKey = RandomVerseScreenNavigationDestination::class.screenKey

    val onDispatch by rememberUpdatedState<(RandomVerseToolbarAction) -> Unit> { action ->
        onAction(RandomVerseAction.ToolbarClicked(toolbarAction = action))
    }

    LaunchedEffect(screenKey) {
        toolbarActionBus.setActiveScreenKey(screenKey)
        toolbarActionBus.events.collect { event ->
            if (event.screenKey != screenKey) return@collect
            val action = event.action as? RandomVerseToolbarAction ?: return@collect
            onDispatch(action)
        }
    }
}


@Composable
private fun RandomVerseSideEffectsCollector(
    effects: Flow<RandomVerseEffect>,
    onCopyToClipboard: () -> Unit,
    onCorrectAnswer: () -> Unit,
    onWrongAnswer: () -> Unit,
    onWrongAnswerOnChapter: () -> Unit,
    onCorrectAnswerOnChapter: () -> Unit,
    onOpenSeveralRandomVerses: () -> Unit,
    onOpenRandomVerseSettings: () -> Unit,
    onOpenVerseWidget: () -> Unit,
    onNotifyEmptyMikud: () -> Unit,
    onDismissSnackbar: () -> Boolean,
) {
    LaunchedEffect(Unit) {
        effects.collect { effect ->
            when (effect) {
                is RandomVerseEffect.CopyToClipboard -> onCopyToClipboard()
                RandomVerseEffect.NotifyCorrectAnswer -> onCorrectAnswer()
                RandomVerseEffect.NotifyWrongAnswer -> onWrongAnswer()
                RandomVerseEffect.OpenSeveralRandomVerses -> onOpenSeveralRandomVerses()
                RandomVerseEffect.OpenRandomVerseSettings -> onOpenRandomVerseSettings()
                RandomVerseEffect.OpenVerseWidget -> onOpenVerseWidget()
                RandomVerseEffect.NotifyWrongAnswerOnChapter -> onWrongAnswerOnChapter()
                RandomVerseEffect.NotifyCorrectAnswerOnChapter -> onCorrectAnswerOnChapter()
                RandomVerseEffect.NotifyEmptyMikud -> onNotifyEmptyMikud()
                RandomVerseEffect.DismissSnackbar -> onDismissSnackbar()
            }
        }
    }
}

/**
 * Pure UI: no store access, no side effects.
 * Only uses state + callbacks.
 */
@Composable
private fun RandomVerseScreenUi(
    state: RandomVerseState,
    animationState: AnimationState,
    animateWrongAnswer: Boolean,
    resetWrongAnswerAnimation: () -> Unit,
    onVerseClick: () -> Unit,
    onVerseLongClick: () -> Unit,
    onMikudClick: () -> Unit,
    onBookClick: (String) -> Unit,
    onWidgetPromptClick: () -> Unit,
    onWidgetPromptDismiss: () -> Unit,
    isCorrect: (String) -> Boolean,
    dialogContent: @Composable () -> Unit,
    bottomSheetContent: @Composable () -> Unit,
) {
    val verseUiModel = state.randomizedVerse.verseUiModel
    val pasukText = state.randomizedVerseAsAnnotatedString

    Column {
        RandomVerseBody(
            verseUiModel = verseUiModel,
            pasukText = pasukText,
            bookItems = state.bookItems,
            animationState = animationState,
            animateWrongAnswer = animateWrongAnswer,
            resetWrongAnswerAnimation = resetWrongAnswerAnimation,
            onVerseClick = onVerseClick,
            onVerseLongClick = onVerseLongClick,
            onMikudClick = onMikudClick,
            onBookClick = onBookClick,
            // Never next to a dialog or the chapter sheet, which the user opened and is busy with
            isWidgetPromptVisible = state.isWidgetPromptVisible &&
                state.dialogState == RandomVerseState.DialogState.None &&
                state.bottomSheetState == BottomSheetState.Hidden,
            onWidgetPromptClick = onWidgetPromptClick,
            onWidgetPromptDismiss = onWidgetPromptDismiss,
            isCorrect = isCorrect,
        )

        dialogContent()
        bottomSheetContent()
    }
}

@Composable
private fun RandomVerseBody(
    verseUiModel: VerseUiModel,
    pasukText: AnnotatedString,
    bookItems: List<Pair<BookData, Boolean>>,
    animationState: AnimationState,
    animateWrongAnswer: Boolean,
    resetWrongAnswerAnimation: () -> Unit,
    onVerseClick: () -> Unit,
    onVerseLongClick: () -> Unit,
    onMikudClick: () -> Unit,
    onBookClick: (String) -> Unit,
    isWidgetPromptVisible: Boolean,
    onWidgetPromptClick: () -> Unit,
    onWidgetPromptDismiss: () -> Unit,
    isCorrect: (String) -> Boolean,
) {
    val verseAnimationModifier = rememberRandomVerseAnimationModifier(
        verseUiModel = verseUiModel,
        animationState = animationState,
        animateWrongAnswer = animateWrongAnswer,
        resetWrongAnswerAnimation = resetWrongAnswerAnimation,
    )

    Column {
        RandomVerseParchmentCard(
            modifier = verseAnimationModifier,
            pasukText = pasukText,
            onClick = onVerseClick,
            onLongClick = onVerseLongClick,
        )

        VerseWidgetPromptChip(
            isVisible = isWidgetPromptVisible,
            onClick = onWidgetPromptClick,
            onDismiss = onWidgetPromptDismiss,
        )

        RandomVerseBooksPicker(
            items = bookItems,
            onMikudClick = onMikudClick,
            onBookClick = onBookClick,
            isCorrect = isCorrect,
        )
    }
}

@Composable
private fun rememberRandomVerseAnimationModifier(
    verseUiModel: VerseUiModel,
    animationState: AnimationState,
    animateWrongAnswer: Boolean,
    resetWrongAnswerAnimation: () -> Unit,
): Modifier {
    var scaleAnimation by remember { mutableStateOf(animationState) }

    val scaleModifier = rememberScaleAnimation(
        animationState = scaleAnimation,
        resetScaleAnimation = { scaleAnimation = AnimationState.IDLE },
    )

    val shakeModifier = rememberShakeAnimation(
        animateWrongAnswer = animateWrongAnswer,
        resetAnimateWrongAnswer = resetWrongAnswerAnimation,
    )

    LaunchedEffect(verseUiModel) {
        scaleAnimation = if (scaleAnimation == AnimationState.IDLE) {
            AnimationState.OFF
        } else {
            AnimationState.RUNNING
        }
    }

    return Modifier.then(scaleModifier).then(shakeModifier)
}

@Composable
private fun RandomVerseParchmentCard(
    modifier: Modifier,
    pasukText: AnnotatedString,
    onClick: () -> Unit,
    onLongClick: () -> Unit,
) {
    ParchmentView(
        modifier = modifier,
        pasukText = pasukText,
        onVerseClick = onClick,
        onVerseLongClick = onLongClick,
    )
}

@Composable
private fun RandomVerseBooksPicker(
    items: List<Pair<BookData, Boolean>>,
    onMikudClick: () -> Unit,
    onBookClick: (String) -> Unit,
    isCorrect: (String) -> Boolean,
) {
    BooksGridLayout(
        items = items,
        onMikudClick = onMikudClick,
        onBookClick = onBookClick,
        isCorrect = isCorrect,
    )
}


private class BottomSheetStateProvider : PreviewParameterProvider<BottomSheetState> {
    override val values = sequenceOf(
        BottomSheetState.Hidden,
        BottomSheetState.ChapterPicker(
            SpannableStringKMP(
                charSequence = List(50) {
                    "פרק ${it.plus(1).toOtiot().addGershaim()}"
                }.joinToString(separator = "  "),
                spannedRangeList = persistentListOf(),
            )
        )
    )
}

@Preview(showBackground = true)
@Composable
private fun RandomVerseScreenPreview(
    @PreviewParameter(BottomSheetStateProvider::class) bottomSheetState: BottomSheetState,
) {
    val items = listOf(mikudItem) + BibleCatalog.books
    val enabledList = MutableList(items.size) { true }

    val pasukText = stringResource(SharedRes.strings.pasuk_example)

    val state = remember(items, enabledList, pasukText) {
        RandomVerseState(
            bookItems = items.zip(enabledList).toImmutableList(),
            verseUiModel = null,
            randomizedVerse = RandomizedVerse(
                verseUiModel = VerseUiModel(
                    id = "preview",
                    text = pasukText,
                    source = "",
                    highlightedRanges = persistentListOfVerseUiModel(),
                    bookIndex = 0,
                    chapterIndex = 0,
                    verse = "",
                ),
                halfSpannableStringKMP = SpannableStringKMP(charSequence = pasukText, spannedRangeList = persistentListOf()),
                fullSpannableStringKMP = SpannableStringKMP(charSequence = pasukText, spannedRangeList = persistentListOf()),
            ),
            shownPart = RandomVersePart.FULL,
            dialogState = RandomVerseState.DialogState.None,
            bottomSheetState = bottomSheetState,
            isShakeEnabled = true,
        )
    }
    PreviewTheme {
        RandomVerseScreenUi(
            state = state,
            animationState = AnimationState.RUNNING,
            animateWrongAnswer = false,
            resetWrongAnswerAnimation = {},
            onVerseClick = {},
            onVerseLongClick = {},
            onMikudClick = {},
            onBookClick = {},
            onWidgetPromptClick = {},
            onWidgetPromptDismiss = {},
            isCorrect = { true },
            dialogContent = {},
            bottomSheetContent = {
                RandomVerseChapterPickerSheet(
                    bottomSheetState = state.bottomSheetState,
                    onAction = {},
                )
            },
        )
    }
}
